import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot } from './shared';

/**
 * Sky Ace's shots (the Barnstormer set's gun, ADR-0160 amended): a tracer (a hot slug, a corkscrew of white smoke
 * trailing it), a ring of air and a flash at the muzzle, a flak burst where it lands (an orange flash in a black puff,
 * shrapnel sparks, the puff drifting); and the flourish's parachute (`barnstormer_chute`, swaying).
 */

/** The tracer, its head at (23, 8): a white-hot slug in a gold glow, a corkscrew of smoke trailing it. */
const tracer = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot((x - 22.5) / 3, (y - 8) / 1.6);
    if (d < 0.55) return 'w';
    if (d < 1) return 'y';
    if (x < 20 && x > 2) {
      const wy = 8 + Math.sin(x * 0.75 + f * 1.57) * 2.2;
      if (Math.abs(y - wy) < 0.9) return x > 13 ? 'w' : x > 7 ? '5' : '4';
    }
    return null;
  });

/** A flash, then a ring of air rolling out of the muzzle, and smoke. */
const ring = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 6, y - 20);
    if (f === 0) return d < 3 ? 'w' : d < 6 ? 'y' : d < 7.5 ? 'o' : null;
    const [cx, rx, ry] = ([0, [13, 3, 8], [21, 3.5, 11]] as const)[f] as readonly [number, number, number];
    const e = Math.hypot((x - cx) / rx, (y - 20) / ry);
    if (e < 1 && e > 0.65) return f === 1 ? 'w' : '5';
    if (Math.hypot(x - 6 - f * 2, y - 20) < 3 - f * 0.6) return f === 1 ? '4' : '3';
    return null;
  });

/** A flak burst: a flash, then a black puff with fire in its heart and shrapnel, drifting and thinning. */
const flak = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) return d < 4 ? 'w' : d < 7 ? 'y' : d < 9 ? 'o' : null;
    // The puff: lumps of black smoke round the burst.
    const R = ([0, 9, 11, 12] as const)[f] as number;
    const lumps = R * (0.85 + 0.15 * Math.sin(Math.atan2(dy, dx) * 5 + f));
    if (d < lumps) {
      if (f === 1 && d < 4) return 'y';
      if (f < 3 && d < R * 0.45) return f === 1 ? 'o' : 'u';
      return dx + dy < -R * 0.5 ? '2' : f === 3 ? '2' : '1';
    }
    // Shrapnel: sparks flung out.
    if (f < 3)
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + 0.3;
        const r = R + 3 + f * 4;
        if (Math.hypot(x - 22 - Math.cos(a) * r, y - 22 - Math.sin(a) * r) < 0.9) return f === 1 ? 'y' : 'o';
      }
    return null;
  });

/** The parachute (24 × 28, its middle at (12, 14)): a striped canopy, its cords to a little pilot, swaying. */
const chute = (f: number) => (c: PixelCanvas) => {
  const sway = ([0, 1, 0, -1] as const)[f] as number;
  paint(c, (x, y) => {
    // The canopy: a dome striped white and red.
    const [cx, cy] = [12 + sway, 10];
    const e = Math.hypot((x - cx) / 10, (y - cy) / 7);
    if (y < cy + 1 && e < 1) {
      if (e > 0.85) return x < cx ? 'r' : 'R';
      const stripe = Math.floor((x - cx + 10) / 4) % 2 === 0;
      return stripe ? (y < cy - 3 ? 'w' : '5') : y < cy - 3 ? 'r' : 'R';
    }
    // Its scalloped hem.
    if (y >= cy + 1 && y < cy + 2 && Math.abs(x - cx) < 10 && Math.floor(x - cx + 10) % 4 !== 0) return 'R';
    // The cords down to the pilot.
    for (const s of [-8, -3, 3, 8]) {
      const t = (y - cy - 2) / 9;
      if (t >= 0 && t <= 1 && Math.abs(x - (cx + s * (1 - t) - sway * t)) < 0.5) return '4';
    }
    // The pilot: a brown helmet, goggles, a little body.
    const [px, py] = [12, 23];
    if (Math.hypot(x - px, y - py + 1.5) < 2.4) return y < py - 2 ? 'm' : x > px ? 'c' : 'n';
    if (Math.abs(x - px) < 1.6 && y > py + 0.5 && y < py + 4) return 'n';
    return null;
  });
};

export default [
  sprite({
    name: 'flak_shot',
    ...shot,
    notes: `Sky Ace's bullet (ADR-0160 amended): a tracer, a white-hot slug in a gold glow at the pellet's head, a corkscrew
of smoke trailing it (white, then grey), turning over 4 frames. Turned along its flight. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(tracer) } },
  }),
  sprite({
    name: 'flak_muzzle',
    ...muzzle,
    notes: `Sky Ace's muzzle flash (ADR-0160 amended): a white, gold and orange flash, then a ring of air rolling out of the
muzzle (white, then grey) over a puff of smoke. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(ring) } },
  }),
  sprite({
    name: 'flak_burst',
    ...burst,
    notes: `Sky Ace's hit (ADR-0160 amended): a flak burst, a white and gold flash, then a lumpy black puff with fire in its
heart (orange, then rust) and shrapnel sparks flung out of it, the puff growing grey and empty. 4 frames. Rubric (4×): S4
R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(flak) } },
  }),
  sprite({
    name: 'barnstormer_chute',
    group: 'fx',
    size: [24, 28],
    origin: [12, 14],
    density: 4,
    notes: `The Barnstormer's knock-out parachute (ADR-0160 amended): a canopy striped white and red with a scalloped hem,
four cords down to a little pilot in a brown helmet (the team's goggles left cyan for both), swaying over 4 frames.
Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 4, frames: frames4(chute) } },
  }),
];
